import "@/App.css";
import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "@/components/ui/sonner";
import AppLayout from "@/components/AppLayout";
import { Loader2 } from "lucide-react";

import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Products from "@/pages/Products";
import Purchases from "@/pages/Purchases";
import OCRImport from "@/pages/OCRImport";
import Requests from "@/pages/Requests";
import Invoices from "@/pages/Invoices";
import InvoiceDetail from "@/pages/InvoiceDetail";
import Customers from "@/pages/Customers";
import CustomerProfile from "@/pages/CustomerProfile";
import Commissions from "@/pages/Commissions";
import Reports from "@/pages/Reports";
import Users from "@/pages/Users";
import AuditLog from "@/pages/AuditLog";
import AdminTools from "@/pages/AdminTools";
import EmployeeScan from "@/pages/EmployeeScan";

function FullLoader() {
  return (
    <div className="app-bg min-h-screen flex items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-[hsl(var(--primary))]" />
    </div>
  );
}

function Protected({ children, adminOnly }) {
  const { user, loading, isAdmin } = useAuth();
  if (loading) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && !isAdmin) return <Navigate to="/" replace />;
  return <AppLayout>{children}</AppLayout>;
}

function HomeRedirect() {
  const { user, loading, isAdmin } = useAuth();
  if (loading) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  return isAdmin ? <AppLayout><Dashboard /></AppLayout> : <Navigate to="/scan" replace />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/" element={<HomeRedirect />} />
            <Route path="/scan" element={<Protected><EmployeeScan /></Protected>} />
            <Route path="/products" element={<Protected><Products /></Protected>} />
            <Route path="/requests" element={<Protected><Requests /></Protected>} />
            <Route path="/invoices" element={<Protected><Invoices /></Protected>} />
            <Route path="/invoices/:id" element={<Protected><InvoiceDetail /></Protected>} />
            <Route path="/commissions" element={<Protected><Commissions /></Protected>} />
            {/* Admin-only */}
            <Route path="/purchases" element={<Protected adminOnly><Purchases /></Protected>} />
            <Route path="/import" element={<Protected adminOnly><OCRImport /></Protected>} />
            <Route path="/customers" element={<Protected adminOnly><Customers /></Protected>} />
            <Route path="/customers/:id" element={<Protected adminOnly><CustomerProfile /></Protected>} />
            <Route path="/reports" element={<Protected adminOnly><Reports /></Protected>} />
            <Route path="/users" element={<Protected adminOnly><Users /></Protected>} />
            <Route path="/admin-tools" element={<Protected adminOnly><AdminTools /></Protected>} />
            <Route path="/audit" element={<Protected adminOnly><AuditLog /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
